import React from 'react';
import { Button as AntButton } from 'antd';
import styled from 'styled-components';
import { windowMaxWidth } from '../../ui/Layout/breakpoints';
import { SmallLoadingIndicator } from '../../SvgIcons';

const BUTTON_OPTIONS = {
  default: {
    padding: '10px 20px'
  },
  iconOnlyButton: {
    padding: '0 8px'
  },
  hasIcon: {
    padding: '0 20px 0 15px'
  },
  hasIconRight: {
    padding: '0 15px 0 20px'
  }
};

const BUTTON_TYPES = {
  primary: {
    activeBackgroundColor: 'var(--primary-oqio-blue)',
    backgroundColor: 'var(--primary-oqio-blue)',
    hoverBackgroundColor: 'var(--primary-oqio-blue-hover)',
    color: 'var(--greyscale-white)',
    hoverFontColor: 'var(--greyscale-white)',
    border: '1px solid var(--primary-oqio-blue)',
    hoverBorderColor: '1px solid var(--primary-oqio-blue-hover)'
  },
  secondary: {
    activeBackgroundColor: 'var(--greyscale-white)',
    backgroundColor: 'var(--greyscale-white)',
    hoverBackgroundColor: 'var(--primary-oqio-blue-hover-transparent)',
    color: 'var(--primary-oqio-blue)',
    hoverFontColor: 'var(--primary-oqio-blue)',
    border: '2px solid var(--primary-oqio-blue)',
    hoverBorderColor: '2px solid var(--primary-oqio-blue)'
  },
  secondaryTransparent: {
    activeBackgroundColor: 'transparent',
    backgroundColor: 'transparent',
    hoverBackgroundColor: 'var(--primary-oqio-blue-hover-transparent)',
    color: 'var(--primary-oqio-blue)',
    hoverFontColor: 'var(--primary-oqio-blue)',
    border: '2px solid var(--primary-oqio-blue)',
    hoverBorderColor: '2px solid var(--primary-oqio-blue)'
  },
  disabled: {
    activeBackgroundColor: 'var(--greyscale-porcelain)',
    backgroundColor: 'var(--greyscale-porcelain)',
    border: '1px solid var(--greyscale-dark-white)',
    color: 'var(--greyscale-light-grey)',
    cursor: 'not-allowed'
  },
  navigation: {
    activeBackgroundColor: 'var(--greyscale-white)',
    backgroundColor: 'transparent',
    hoverBackgroundColor: 'var(--primary-oqio-blue-hover-transparent)',
    color: 'var(--primary-oqio-blue)',
    hoverFontColor: 'var(--primary-oqio-blue)',
    border: '2px solid var(--primary-oqio-blue)',
    hoverBorderColor: '2px solid var(--primary-oqio-blue)'
  }
};

const StyledButton = styled(AntButton)`
  /* stylelint-disable no-descending-specificity */
  align-items: center;
  background-color: ${(props) =>
    props.danger && props.type === 'primary' ? 'var(--primary-oqio-red)' : BUTTON_TYPES[props.type]?.backgroundColor};
  border: ${(props) => (props.danger ? '1px solid var(--primary-oqio-red)' : BUTTON_TYPES[props.type]?.border)};
  border-radius: var(--border-radius-small);
  box-shadow: none !important;
  color: ${(props) =>
    props.danger && props.type === 'primary' ? 'var(--greyscale-white)' : BUTTON_TYPES[props.type]?.color};
  cursor: pointer;
  display: inline-flex;
  flex-direction: ${(props) => (props.$iconPlacement === 'end' ? 'row-reverse' : 'row')};
  gap: 6px;
  justify-content: ${(props) => (props.block && props.icon && props.children ? 'space-between' : 'center')};
  outline: inherit;

  &::after {
    content: ${(props) => (props.block && props.icon && props.children ? '""' : 'none')};
    display: block;
    flex-shrink: 0;
    height: 20px;
    width: 20px;
  }

  padding: ${(props) => {
    if (!props.children) {
      return BUTTON_OPTIONS.iconOnlyButton.padding;
    }
    if (props.icon) {
      if (props.$iconPlacement === 'end') {
        return BUTTON_OPTIONS.hasIconRight.padding;
      }
      return BUTTON_OPTIONS.hasIcon.padding;
    }
    return BUTTON_OPTIONS.default.padding;
  }};
  transition:
    color 0.2s,
    background-color 0.1s,
    border-color 0.2s;
  width: ${(props) => (props.fullWidth ? '100%' : 'auto')};

  @media ${windowMaxWidth.mobile} {
    padding: 10px;
  }

  ${(props) =>
    props.$hideTextOnSplitScreen &&
    `
    @media ${windowMaxWidth.splitScreen} {
      padding: ${BUTTON_OPTIONS.iconOnlyButton.padding};
    }
  `}

  svg {
    path,
    circle {
      stroke: ${(props) =>
        props.danger && props.type === 'primary' ? 'var(--greyscale-white)' : BUTTON_TYPES[props.type]?.color};
    }

    rect {
      fill: ${(props) =>
        props.danger && props.type === 'primary' ? 'var(--greyscale-white)' : BUTTON_TYPES[props.type]?.color};
    }
  }

  &:active {
    background-color: ${(props) =>
      props.danger && props.type === 'primary'
        ? 'var(--primary-oqio-red)'
        : BUTTON_TYPES[props.type]?.activeBackgroundColor};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-oqio-blue-hover);
    outline-offset: 2px;
    transition:
      outline-offset 0s,
      outline 0s;
  }

  &:not(:disabled):not(.ant-btn-disabled):hover {
    background-color: ${(props) =>
      props.danger && props.type === 'primary'
        ? 'var(--primary-oqio-red-hover)'
        : BUTTON_TYPES[props.type]?.hoverBackgroundColor} !important;
    border: ${(props) =>
      props.danger
        ? '1px solid var(--primary-oqio-red-hover)'
        : BUTTON_TYPES[props.type]?.hoverBorderColor || BUTTON_TYPES[props.type]?.border || '0'} !important;
    color: ${(props) =>
      props.danger && props.type === 'primary'
        ? 'var(--greyscale-white)'
        : BUTTON_TYPES[props.type]?.hoverFontColor} !important;
    outline: none;
  }

  &:disabled {
    background-color: var(--greyscale-porcelain);
    border: 1px solid var(--greyscale-dark-white);
    color: var(--greyscale-concrete);
    cursor: default;

    .ant-btn-icon {
      svg {
        path,
        circle {
          stroke: var(--greyscale-concrete);
        }
      }
    }
  }

  .ant-btn-icon {
    height: 20px;
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 20px;
  }

  &.modal-header-button {
    border: 0;
    position: relative;

    &:hover {
      background-color: var(--secondary-whisper);
      border: 0;
    }

    svg {
      path,
      circle {
        stroke: var(--greyscale-white);
      }

      rect {
        fill: #111;
      }
    }
  }

  &.btn {
    &-icon-only {
      padding: 8px;

      .ant-btn-icon {
        margin-right: 0 !important;
      }
    }

    &-worklist {
      margin-left: 10px;
      position: relative;

      &:hover {
        background-color: var(--primary-oqio-blue-hover-transparent);
      }

      @media ${windowMaxWidth.mobile} {
        width: 30px;
        height: 30px;
      }
    }

    &-claim-status {
      margin-right: 2px;

      @media ${windowMaxWidth.mobile} {
        display: flex;
        margin: 0;
        height: 30px;
        padding: 0;
        width: 30px;
        align-items: center;
        justify-content: center;

        > span:not(.ant-btn-icon) {
          display: none;
        }
      }
    }

    &-dropdown-claim-status {
      display: flex;
      justify-content: flex-start;
    }

    &-icon-right {
      flex-direction: row-reverse;
      height: 20px;
      padding: 0;

      .ant-btn-icon {
        margin-left: 6px !important;
        margin-right: 0 !important;
      }
    }

    &-worklist-message-attachment {
      color: var(--greyscale-concrete);

      .ant-btn-icon {
        svg {
          path {
            stroke: var(--greyscale-concrete);
          }
        }
      }
    }

    &-general-message-attachment {
      display: flex;
      justify-content: space-between !important;

      &:disabled {
        color: var(--greyscale-concrete) !important;
      }
    }
  }

  > span:not(.ant-btn-icon) {
    line-height: var(--font-line-height-regular);

    @media ${windowMaxWidth.mobile} {
      display: ${(props) => (props.$hideTextOnMobile ? 'none' : 'block')};
    }

    @media ${windowMaxWidth.splitScreen} {
      display: ${(props) => (props.$hideTextOnSplitScreen ? 'none' : 'block')};
    }
  }

  &.ant-btn-link {
    border: none;
    color: var(--primary-oqio-blue);
    height: 20px;
    min-width: 0;
    padding: 0;

    &.multiline-link {
      height: auto;
      white-space: normal;
      text-align: left;
      display: flex;
      align-items: flex-start;
    }

    svg {
      path {
        stroke: var(--primary-oqio-blue);

        &.fill-path {
          fill: var(--primary-oqio-blue);
        }
      }

      circle {
        stroke: var(--primary-oqio-blue);
      }
    }

    &:not(:disabled):not(.ant-btn-disabled):hover {
      color: var(--primary-oqio-blue-hover);

      svg {
        path {
          stroke: var(--primary-oqio-blue-hover);
        }

        circle {
          stroke: var(--primary-oqio-blue-hover);
        }
      }
    }

    &.ant-btn-dangerous {
      border: none !important;
      color: var(--primary-oqio-red);

      svg {
        path {
          stroke: var(--primary-oqio-red);
        }

        circle {
          stroke: var(--primary-oqio-red);
        }
      }

      &:not(:disabled):not(.ant-btn-disabled):hover {
        border: none !important;
        color: var(--primary-oqio-red-hover);

        svg {
          path {
            stroke: var(--primary-oqio-red-hover);
          }

          circle {
            stroke: var(--primary-oqio-red-hover);
          }
        }
      }
    }

    &:disabled,
    &.ant-btn-color-link.ant-btn-variant-link:disabled {
      background-color: transparent;
      color: var(--greyscale-concrete);
      cursor: default;

      svg {
        path {
          stroke: var(--greyscale-concrete);
        }

        circle {
          stroke: var(--greyscale-concrete);
        }
      }
    }
  }

  &.ant-btn-text {
    border: 1px solid transparent;
    color: var(--primary-oqio-blue);

    svg {
      path {
        stroke: var(--primary-oqio-blue);
      }

      circle {
        stroke: var(--primary-oqio-blue);
      }

      rect {
        stroke: var(--primary-oqio-blue);
      }
    }

    &:not(:disabled):not(.ant-btn-disabled):hover {
      border: 1px solid transparent !important;
      color: var(--primary-oqio-blue);

      svg {
        path {
          stroke: var(--primary-oqio-blue);
        }

        circle {
          stroke: var(--primary-oqio-blue);
        }

        rect {
          stroke: var(--primary-oqio-blue);
        }
      }
    }

    &.ant-btn-dangerous {
      border: 1px solid transparent !important;
      color: var(--primary-oqio-red);

      &:not(:disabled):not(.ant-btn-disabled):hover {
        background-color: var(--greyscale-porcelain);
        border: 1px solid transparent !important;
        color: var(--primary-oqio-red);

        svg {
          path {
            stroke: var(--primary-oqio-red);
          }
        }
      }

      svg {
        path {
          stroke: var(--primary-oqio-red);
        }
      }
    }

    &.btn-low-height {
      height: 20px;
    }
  }

  &.btn-overlay {
    background-color: var(--greyscale-white);
    border: none;
    border-radius: var(--border-radius-extra-large);
    box-shadow: 0 1px 8px 0 rgb(17 17 17 / 20%) !important;
    pointer-events: all;

    &:hover:not(:disabled) {
      background-color: var(--greyscale-porcelain);
      border: none !important;
      border-radius: var(--border-radius-extra-large);
      color: var(--primary-oqio-blue);

      svg {
        path {
          stroke: ${(props) => BUTTON_TYPES[props.type]?.color || 'var(--primary-oqio-blue)'};
          transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
        }

        circle {
          stroke: ${(props) => BUTTON_TYPES[props.type]?.color || 'var(--primary-oqio-blue)'};
          transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
        }
      }
    }

    &.ant-btn[disabled] {
      background-color: var(--greyscale-white);
      border-radius: var(--border-radius-extra-large);
      box-shadow:
        0 0 0 1px rgb(0 0 0 / 10%),
        0 2px 4px rgb(0 0 0 / 10%) !important;

      svg {
        path {
          stroke: var(--greyscale-concrete);
        }

        circle {
          stroke: var(--greyscale-concrete);
        }
      }

      &:hover {
        background-color: var(--greyscale-white);
        border-radius: var(--border-radius-extra-large);
        box-shadow:
          0 0 0 1px rgb(0 0 0 / 10%),
          0 2px 4px rgb(0 0 0 / 10%) !important;
      }
    }
  }

  &.btn-action-list {
    @media ${windowMaxWidth.mobile} {
      padding: 5px;
      height: 30px;

      .ant-btn-icon {
        height: 20px;
      }

      span:not(.ant-btn-icon) {
        display: none;
      }
    }
  }

  &.button-inbox {
    @media ${windowMaxWidth.mobile} {
      height: 30px;
      width: 30px;

      .ant-btn-icon {
        height: 20px;
      }
    }
  }

  &.btn-worklist-action {
    border: 1px solid var(--greyscale-dark-white);
    height: 30px;
    padding: 5px 10px;

    &:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: var(--greyscale-white) !important;
      border: 1px solid var(--greyscale-dark-white) !important;
      color: var(--primary-oqio-blue-hover) !important;

      svg {
        path {
          stroke: var(--primary-oqio-blue-hover);
          transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
        }

        circle {
          stroke: var(--primary-oqio-blue-hover);
          transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
        }
      }
    }
  }

  &.btn-worklist-new-reminder {
    border-radius: 50%;
    bottom: 16px;
    box-shadow: 0 1px 8px 0 rgb(17 17 17 / 20%) !important;
    height: 60px;
    position: absolute;
    right: 86px;
    width: 60px;
    z-index: 100;

    @media ${windowMaxWidth.mobile} {
      bottom: 26px;
      width: 44px;
      height: 44px;
      left: 16px;
      right: auto;
    }
  }

  &.btn-worklist-new-message {
    border-radius: 50%;
    bottom: 16px;
    box-shadow: 0 1px 8px 0 rgb(17 17 17 / 20%) !important;
    height: 60px;
    position: absolute;
    right: 16px;
    width: 60px;
    z-index: 100;

    @media ${windowMaxWidth.mobile} {
      bottom: 26px;
      width: 44px;
      height: 44px;
    }
  }

  &.btn-worklist-changes {
    border-radius: 20px;
    box-shadow: 0 1px 8px 0 rgb(17 17 17 / 20%) !important;
    height: 30px;
    padding: 10px 5px 10px 10px;
    transition: all 0.3s ease;
    z-index: 100;

    svg:nth-of-type(1) {
      height: 16px;
      transform: rotate(180deg);
      width: 20px;
    }

    &.sticky {
      height: 40px;
      padding: 15px 10px 15px 15px;
      transition: all 0.3s ease;

      svg:nth-of-type(1) {
        transform: unset;
      }

      svg:nth-of-type(2) {
        path,
        circle {
          stroke: unset;
        }
      }
    }
  }

  &.btn-worklist-history-change-link {
    font-size: var(--font-size-small);
    height: auto;
    overflow-wrap: anywhere;
    text-align: start;
    white-space: normal;
    word-break: normal;
  }

  &.btn-worklist-history-change-title {
    font-size: var(--font-size-medium);
    font-weight: var(--font-weight-semi-bold);
    height: auto;
    overflow-wrap: anywhere;
    text-align: left;
    white-space: normal;
    word-break: normal;
  }
`;

/**
 * `Button` renders a button with styles and behaviors applied based on the provided props.
 * It integrates with Ant Design's Button component and applies additional styling with styled-components.
 * Depending on the `type`, `icon`, `children`, and other props, it adapts its appearance and functionality.
 *
 * @param {object} props - The properties passed to the button component.
 * @param {string} props.className - CSS class for additional styling.
 * @param {ReactNode} props.children - Content inside the button, usually text or icons.
 * @param {boolean} props.disabled - If true, the button will be disabled.
 * @param {boolean} [props.loading=false] - If true, shows a loading indicator.
 * @param {Function} props.onClick - Callback function when the button is clicked.
 * @param {string} [props.type='primary'] - Defines the button's style type (e.g., primary, secondary).
 * @param {boolean} [props.block=false] - If true, the button will fit to the width of its parent.
 * @param {boolean} props.icon - If true, modifies the padding to better fit an icon.
 * @param {boolean} props.danger - If true, applies a danger style to the button.
 * @param {string} props.label - Text label for the button.
 * @param {boolean} props.hideTextOnMobile - If true, hides the text on mobile devices.
 * @param {boolean} props.hideTextOnSplitScreen - If true, hides the text at split-screen breakpoint and below.
 */
const ButtonComponent = ({
  className = '',
  children = null,
  disabled = false,
  loading = false,
  onClick,
  type = 'primary',
  block = false,
  icon,
  danger = false,
  label = null,
  hideTextOnMobile = false,
  hideTextOnSplitScreen = false,
  dataIntercomTarget = null,
  iconPlacement = 'start',
  href,
  target,
  rel,
  ...restProps
}) => {
  return (
    <StyledButton
      className={className}
      disabled={disabled}
      onClick={onClick}
      type={type}
      loading={loading && { icon: <SmallLoadingIndicator /> }}
      block={block}
      icon={icon}
      danger={danger}
      data-intercom-target={dataIntercomTarget}
      $hideTextOnMobile={hideTextOnMobile}
      $hideTextOnSplitScreen={hideTextOnSplitScreen}
      $iconPlacement={iconPlacement}
      href={href}
      target={target}
      rel={target === '_blank' ? (rel ?? 'noopener noreferrer') : rel}
      {...restProps}
    >
      {label ? label : children}
    </StyledButton>
  );
};

export const Button = ButtonComponent;
